// validateSolveZip.ts — client-side sanity check for a Cloud Solve photo zip,
// before it's uploaded to R2.
//
// Expected layout (see course_1_demo.zip), optionally nested under one root folder:
//   <root>/sparse/0/{cameras,images,points3D}.bin   COLMAP sparse (camera poses)
//   <root>/camNN/<number>.jpg                        per-camera image sequence
//
// Rules:
//   1. must contain camNN/ folders with images
//   2. camera image names must be numeric (0.jpg, 1.jpg, …)
//   3. if sparse/0 exists, its registered cameras should match the camNN folders
//   4. if sparse/0 is missing → warn (poses will be auto-estimated during solving)
//
// Only sparse/0/images.bin is decompressed (via fflate's filter); the (large)
// image files are never inflated — we validate them by name from the zip index.

import { unzip, type Unzipped, type UnzipFileInfo } from 'fflate'

export interface SolveZipValidation {
  valid: boolean       // false → block upload (hard errors)
  errors: string[]     // blocking problems
  warnings: string[]   // non-blocking (e.g. missing sparse, minor inconsistency)
  hasSparse: boolean
  camCount: number
  imageCount: number
  skipped?: boolean    // true → too large to introspect in-browser
}

// Above this size we skip the in-browser parse (arrayBuffer would be too big).
const INTROSPECT_LIMIT = 1.5 * 1024 * 1024 * 1024 // 1.5 GB
const IMG_RE = /\.(jpe?g|png)$/i
const NUMERIC_IMG_RE = /^\d+\.(jpe?g|png)$/i

function esc(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// COLMAP images.bin → the set of camera numeric ids referenced by image names
// (e.g. "cam04.png" → 4). Layout per image: id(u32) qvec(4×f64) tvec(3×f64)
// camera_id(u32) name(\0-terminated) num_points2D(u64) points(num×24 bytes).
function parseColmapImageCamIds(data: Uint8Array): Set<number> {
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength)
  const dec = new TextDecoder()
  const ids = new Set<number>()
  let off = 0
  const num = Number(dv.getBigUint64(off, true)); off += 8
  for (let i = 0; i < num; i++) {
    off += 4          // image_id
    off += 8 * 7      // qvec(4) + tvec(3)
    off += 4          // camera_id
    const start = off
    while (off < data.length && data[off] !== 0) off++
    const name = dec.decode(data.subarray(start, off))
    off++             // null terminator
    const npts = Number(dv.getBigUint64(off, true)); off += 8
    off += npts * 24  // (x:f64, y:f64, point3D_id:u64)
    const m = name.match(/cam(\d+)/i)
    if (m) ids.add(parseInt(m[1], 10))
  }
  return ids
}

export async function validateSolveZip(file: File): Promise<SolveZipValidation> {
  const res: SolveZipValidation = {
    valid: true, errors: [], warnings: [], hasSparse: false, camCount: 0, imageCount: 0,
  }

  if (file.size > INTROSPECT_LIMIT) {
    res.skipped = true
    res.warnings.push('File is very large; its contents will be validated during solving instead of here.')
    return res
  }

  let buf: Uint8Array
  try {
    buf = new Uint8Array(await file.arrayBuffer())
  } catch {
    res.skipped = true
    res.warnings.push('Could not read the file for validation; it will be validated during solving.')
    return res
  }

  const names: string[] = []
  let out: Unzipped
  try {
    out = await new Promise<Unzipped>((resolve, reject) =>
      unzip(
        buf,
        {
          filter: (f: UnzipFileInfo) => {
            names.push(f.name)
            return /(?:^|\/)sparse\/0\/images\.bin$/i.test(f.name)
          },
        },
        (err, o) => (err ? reject(err) : resolve(o)),
      ),
    )
  } catch {
    res.valid = false
    res.errors.push('Not a valid .zip archive, or it is corrupted.')
    return res
  }

  const clean = names.filter((n) => !n.includes('__MACOSX') && !n.endsWith('.DS_Store'))

  // Root prefix = path before "sparse/0/" (or before "camNN/" if no sparse).
  let root = ''
  const sparseEntry = clean.find((n) => /(?:^|\/)sparse\/0\//i.test(n))
  if (sparseEntry) {
    root = sparseEntry.slice(0, sparseEntry.toLowerCase().indexOf('sparse/0/'))
  } else {
    const camEntry = clean.find((n) => /(?:^|\/)cam\d+\//i.test(n))
    const m = camEntry?.match(/^(.*?)cam\d+\//i)
    root = m ? m[1] : ''
  }

  res.hasSparse = clean.some((n) =>
    new RegExp(`^${esc(root)}sparse/0/(images|cameras)\\.bin$`, 'i').test(n),
  )

  // Collect cam folders + their image file basenames.
  const camFileRe = new RegExp(`^${esc(root)}(cam(\\d+))/([^/]+)$`, 'i')
  const camImages = new Map<number, string[]>() // camId -> basenames
  for (const n of clean) {
    const m = n.match(camFileRe)
    if (!m) continue
    const id = parseInt(m[2], 10)
    if (!camImages.has(id)) camImages.set(id, [])
    if (m[3]) camImages.get(id)!.push(m[3])
  }

  res.camCount = camImages.size
  const badNames: string[] = []
  for (const [id, files] of camImages) {
    for (const f of files) {
      if (!IMG_RE.test(f)) continue // ignore stray non-image files
      res.imageCount++
      if (!NUMERIC_IMG_RE.test(f)) badNames.push(`cam${id}/${f}`)
    }
  }

  // Rule 1 — must have camera folders with images.
  if (res.camCount === 0 || res.imageCount === 0) {
    res.valid = false
    res.errors.push(
      'No camera images found. Expected camXX/ folders each containing numbered images (e.g. cam00/0.jpg).',
    )
  }

  // Rule 2 — numeric image names.
  if (badNames.length) {
    res.valid = false
    const sample = badNames.slice(0, 5).join(', ')
    res.errors.push(
      `Camera images must be named as numbers (e.g. 0.jpg, 1.jpg). ${badNames.length} file(s) violate this, e.g.: ${sample}${badNames.length > 5 ? ', …' : ''}`,
    )
  }

  if (!res.hasSparse) {
    // Rule 4 — no camera info: warn, don't block.
    res.warnings.push(
      'No sparse/0 camera info found. Camera poses will be auto-estimated during solving, but not all cameras are guaranteed to be recovered — this may affect quality.',
    )
  } else {
    // Rule 3 — cross-check sparse cameras against camXX folders.
    const key = Object.keys(out).find((k) => /(?:^|\/)sparse\/0\/images\.bin$/i.test(k))
    const binData = key ? out[key] : undefined
    if (binData && binData.length) {
      try {
        const sparseIds = parseColmapImageCamIds(binData)
        const folderIds = new Set(camImages.keys())
        const missingFolders = [...sparseIds].filter((c) => !folderIds.has(c))
        const extraFolders = [...folderIds].filter((c) => !sparseIds.has(c))
        if (missingFolders.length || extraFolders.length) {
          const parts: string[] = []
          if (missingFolders.length) {
            parts.push(
              `${missingFolders.length} camera(s) in sparse/0 have no matching folder (e.g. ${missingFolders.slice(0, 5).map((c) => `cam${c}`).join(', ')})`,
            )
          }
          if (extraFolders.length) {
            parts.push(
              `${extraFolders.length} camera folder(s) are not in sparse/0 (e.g. ${extraFolders.slice(0, 5).map((c) => `cam${c}`).join(', ')})`,
            )
          }
          res.warnings.push(`sparse/0 and camera folders are not fully consistent: ${parts.join('; ')}.`)
        }
      } catch {
        res.warnings.push('sparse/0/images.bin could not be parsed for the consistency check.')
      }
    }
  }

  return res
}
